import { useCallback, useEffect, useState } from "react";
import { beginProviderAuth, getAccountProviders, type AccountProvider, type AccountProviderStatus } from "../auth";
import { Dialog } from "./Dialog";
import { Icon } from "./Icon";
import { TurnstileWidget } from "./TurnstileWidget";

export function AuthDialog({ open, onClose, initialError = "" }: { open: boolean; onClose: () => void; initialError?: string }) {
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [token, setToken] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const [providers, setProviders] = useState<AccountProviderStatus[] | null>(null);
  const receiveToken = useCallback((value: string) => setToken(value), []);
  const receiveError = useCallback((message: string) => { setError(message); setToken(""); }, []);

  useEffect(() => {
    if (open) {
      setToken("");
      setPending(false);
      setError(initialError);
      setProviders(null);
    }
  }, [initialError, open]);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    let active = true;
    void getAccountProviders(controller.signal)
      .then((nextProviders) => { if (active) setProviders(nextProviders); })
      .catch(() => { if (active) setError("Sign-in options could not be loaded. Try again shortly."); });
    return () => { active = false; controller.abort(); };
  }, [open]);

  async function continueWithProvider(provider: AccountProvider) {
    if (!token || pending) return;
    setPending(true);
    setError("");
    try {
      window.location.assign(await beginProviderAuth(mode, provider, token));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Sign-in could not start.");
      setToken("");
      setPending(false);
    }
  }

  function select(next: "login" | "signup") {
    setMode(next);
    setToken("");
    setError("");
  }

  const configuredProviders = providers?.filter((provider) => provider.configured) ?? [];

  return (
    <Dialog
      open={open}
      title={mode === "login" ? "Sign in to Roam" : "Create your Roam account"}
      description="Keep trips available across your devices with your CodePawl account."
      onClose={onClose}
      className="auth-dialog"
    >
      <div className="auth-content">
        <div className="auth-tabs" role="tablist" aria-label="Account action">
          <button type="button" role="tab" aria-selected={mode === "login"} onClick={() => select("login")}>Sign in</button>
          <button type="button" role="tab" aria-selected={mode === "signup"} onClick={() => select("signup")}>Create account</button>
        </div>
        {open ? <TurnstileWidget action={mode} onToken={receiveToken} onError={receiveError} /> : null}
        {error ? <p className="form-error" role="alert">{error}</p> : null}
        <div className="auth-providers" role="group" aria-label="Sign-in providers">
          {providers === null && !error ? <p className="auth-provider-status" role="status">Checking available providers…</p> : null}
          {configuredProviders.map((provider) => (
            <button className={`button auth-provider auth-provider-${provider.id}`} type="button" key={provider.id} disabled={!token || pending} onClick={() => void continueWithProvider(provider.id)}>
              <Icon name={provider.id} />
              {pending ? "Opening provider…" : `${mode === "signup" ? "Create account with" : "Continue with"} ${provider.id === "github" ? "GitHub" : "Google"}`}
              <Icon name="arrow-right" size={16} />
            </button>
          ))}
          {providers && configuredProviders.length === 0 && !error ? <p className="form-error" role="alert">No sign-in provider is configured.</p> : null}
        </div>
        <p className="auth-footnote">A first sign-in creates your account. CodePawl will ask once before Roam can read your profile and sync trips.</p>
      </div>
    </Dialog>
  );
}
